/* eslint-disable no-extra-boolean-cast */
import { GoogleMapProvider } from '@lemonade-hq/cdk';
import {
  AnalyticsProvider,
  ClientProvider,
  ComplianceProvider,
  I18nProvider,
  InfraProvider,
  UserProvider,
  useSetupCsrf,
  useSetupTracking,
  VisitorProvider,
} from '@lemonade-hq/nuiza';
import type { AppProps } from '@lemonade-hq/nuiza';
import { ThemeProvider } from '@lemonade-hq/uniclient-theme';
import React from 'react';
import { Analytics } from './analytics';
import { ErrorBoundary } from './errorBoundary/ErrorBoundary';
import type { ErrorBoundaryProps } from './errorBoundary/ErrorBoundary';
import { useBlockZoomIn } from './hooks/useBlockZoomIn';
import { useBodyNativeIndication } from './hooks/useBodyNativeIndication';
import { UniclientDialogProvider } from './providers/DialogProvider';
import { FeatureFlagsProvider } from './providers/FeatureFlagsProvider';
import type { FlagsConfig } from './providers/FeatureFlagsProvider';
import { NavProvider } from './providers/NavProvider';
import { RedirectProvider } from './providers/RedirectProvider';
import { UniclientLayoutProvider } from './providers/UniclientLayoutProvider';
import { UniclientTrackingProvider } from './providers/UniclientTrackingProvider';

import './UniclientApp.css';
import { ThemeSelector } from './ThemeSelector';

// MEMOS ********************************************
const MemoizedAnalyticsProvider = React.memo(AnalyticsProvider);
const MemoizedUserProvider = React.memo(UserProvider);
const MemoizedVisitorProvider = React.memo(VisitorProvider);
const MemoizedLayoutProvider = React.memo(UniclientLayoutProvider);
const MemoizedNavProvider = React.memo(NavProvider);
const MemoizedRedirectProvider = React.memo(RedirectProvider);
const MemoizedInfraProvider = React.memo(InfraProvider);
const MemoizedClientProvider = React.memo(ClientProvider);
const MemoizedComplianceProvider = React.memo(ComplianceProvider);
const MemoizedThemeProvider = React.memo(ThemeProvider);
const MemoizedGoogleMapsProvider = React.memo(GoogleMapProvider);
const MemoizedDialogProvider = React.memo(UniclientDialogProvider);
const MemoizedUniclientTrackingProvider = React.memo(UniclientTrackingProvider);
const MemoizedFeatureFlagsProvider = React.memo(FeatureFlagsProvider);

interface UniclientAppProps extends Omit<AppProps, 'monitoring'>, Pick<ErrorBoundaryProps, 'fallback'> {
  readonly isPartytownEnabled?: boolean;
  readonly googleMaps: string;
  readonly flags: FlagsConfig;
}

export const UniclientApp: React.FC<UniclientAppProps> = props => {
  const {
    locale,
    analytics,
    security: { csrf },
    auth,
    user,
    infra,
    nav,
    children,
    isPartytownEnabled = false,
    fallback,
    googleMaps,
    flags,
  } = props;

  useSetupTracking({ locale, trackQuery: analytics.trackQuery, user });
  useSetupCsrf({ csrf });
  useBodyNativeIndication();
  useBlockZoomIn();

  return (
    <I18nProvider {...locale}>
      <ErrorBoundary fallback={fallback}>
        <MemoizedComplianceProvider>
          <MemoizedInfraProvider {...infra}>
            <MemoizedUserProvider initialIdentifyContext={analytics.customerIoContext} initialUser={user}>
              <MemoizedVisitorProvider>
                <MemoizedFeatureFlagsProvider {...flags}>
                  <MemoizedAnalyticsProvider {...analytics}>
                    <MemoizedNavProvider authConfig={auth} navConfig={nav}>
                      <MemoizedUniclientTrackingProvider>
                        <MemoizedRedirectProvider>
                          <MemoizedLayoutProvider>
                            <MemoizedClientProvider>
                              <MemoizedDialogProvider>
                                <MemoizedThemeProvider>
                                  <ThemeSelector>
                                    <MemoizedGoogleMapsProvider googleMapKey={googleMaps}>
                                      <Analytics isPartytownEnabled={isPartytownEnabled} />
                                      {children}
                                      {/* render loginIframe here if loginIframeState.showing from useSetupClientAuth */}
                                    </MemoizedGoogleMapsProvider>
                                  </ThemeSelector>
                                </MemoizedThemeProvider>
                              </MemoizedDialogProvider>
                            </MemoizedClientProvider>
                          </MemoizedLayoutProvider>
                        </MemoizedRedirectProvider>
                      </MemoizedUniclientTrackingProvider>
                    </MemoizedNavProvider>
                  </MemoizedAnalyticsProvider>
                </MemoizedFeatureFlagsProvider>
              </MemoizedVisitorProvider>
            </MemoizedUserProvider>
          </MemoizedInfraProvider>
        </MemoizedComplianceProvider>
      </ErrorBoundary>
    </I18nProvider>
  );
};
